import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Eye, EyeOff, ShieldCheck, ArrowLeft, Loader } from 'lucide-react'
import ForgotPasswordModal from '../components/ForgotPasswordModal'
import { CreateAccountModal } from '../components/CreateAccountModal'
import useAuthStore from '../store/auth-store'
import { consumeSessionExpiredFlash } from '../lib/sessionExpiry'

// ─── Google Authenticator icon ────────────────────────────────────────────────
function GoogleAuthIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" rx="8" fill="#4285F4"/>
      <path d="M24 12C17.4 12 12 17.4 12 24s5.4 12 12 12 12-5.4 12-12S30.6 12 24 12zm0 21.6c-5.3 0-9.6-4.3-9.6-9.6s4.3-9.6 9.6-9.6 9.6 4.3 9.6 9.6-4.3 9.6-9.6 9.6z" fill="white"/>
      <path d="M24 18c-3.3 0-6 2.7-6 6s2.7 6 6 6 6-2.7 6-6-2.7-6-6-6zm0 9.6c-2 0-3.6-1.6-3.6-3.6s1.6-3.6 3.6-3.6 3.6 1.6 3.6 3.6-1.6 3.6-3.6 3.6z" fill="white"/>
    </svg>
  )
}

// ─── 6-box OTP Input ──────────────────────────────────────────────────────────
// `onComplete(fullValue)` fires the moment the user finishes the 6th digit with
// the FRESH value, so callers don't depend on React state having flushed.
function OtpInput({ value, onChange, onComplete, disabled }) {
  const refs = useRef([null, null, null, null, null, null]).current

  const setOtp = (next) => {
    const clean = (next || '').replace(/\D/g, '').slice(0, 6)
    onChange(clean)
    return clean
  }

  const handleKey = (e, idx) => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      if (value[idx]) {
        setOtp(value.substring(0, idx) + value.substring(idx + 1))
      } else if (idx > 0) {
        setOtp(value.substring(0, idx - 1) + value.substring(idx))
        refs[idx - 1]?.focus()
      }
    }
  }

  const handleChange = (e, idx) => {
    const ch = e.target.value.replace(/\D/g, '').slice(-1)
    if (!ch) return
    const padded = value.padEnd(idx, ' ').substring(0, idx)
    const tail   = value.substring(idx + 1)
    const next   = setOtp(padded + ch + tail)
    if (idx < 5) refs[idx + 1]?.focus()
    if (next.length === 6 && onComplete) onComplete(next)
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    const next = setOtp(pasted)
    refs[Math.min(next.length, 5)]?.focus()
    if (next.length === 6 && onComplete) onComplete(next)
  }

  const boxCls = "w-11 h-12 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#358ECD] disabled:bg-gray-100 transition"

  return (
    <div className="flex items-center justify-center gap-2">
      {[0, 1, 2].map(i => (
        <input key={i} ref={el => refs[i] = el} type="text" inputMode="numeric" maxLength={1}
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          disabled={disabled} value={value[i] || ''}
          onChange={e => handleChange(e, i)} onKeyDown={e => handleKey(e, i)} onPaste={handlePaste}
          className={boxCls} />
      ))}
      <span className="text-2xl font-light text-gray-400 mx-1">-</span>
      {[3, 4, 5].map(i => (
        <input key={i} ref={el => refs[i] = el} type="text" inputMode="numeric" maxLength={1}
          autoComplete="off"
          disabled={disabled} value={value[i] || ''}
          onChange={e => handleChange(e, i)} onKeyDown={e => handleKey(e, i)} onPaste={handlePaste}
          className={boxCls} />
      ))}
    </div>
  )
}

// ─── Main LoginPage ───────────────────────────────────────────────────────────
export function LoginPage() {
  const navigate  = useNavigate()
  const location  = useLocation()
  // Where to go after a successful login: back to the page the user was sent
  // here from (e.g. the project dashboard they clicked), else the default.
  const fromLoc = location.state?.from
  const redirectTo = fromLoc
    ? `${fromLoc.pathname || ''}${fromLoc.search || ''}` || '/dashboard'
    : '/dashboard'
  const {
    login, verifyTwoFactor, resendEmail2faCode,
    isLoading, error, isAuthenticated, pendingTwoFactor, clearError,
  } = useAuthStore()

  // Step 1 state
  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')
  const [showPwd,  setShowPwd]  = useState(false)
  const [remember, setRemember] = useState(false)
  const [authMethod, setAuthMethod] = useState('totp')   // 'totp' (Phone) | 'email'

  // Step 2 state
  const [otpValue,       setOtpValue]       = useState('')
  const [useBackupCode,  setUseBackupCode]  = useState(false)
  const [backupCodeValue, setBackupCodeValue] = useState('')
  const [resendCountdown, setResendCountdown] = useState(0)
  const [resendBusy, setResendBusy] = useState(false)
  const [resendMsg, setResendMsg] = useState(null)
  const chosenMethodRef = useRef('totp')   // Sticky copy of the method user picked

  // Modals
  const [showForgot,  setShowForgot]  = useState(false)
  const [showRegister, setShowRegister] = useState(false)

  // One-shot flash from the global session-expiry interceptor.
  // Surfaced on the login page so the user understands WHY they were
  // bounced here, instead of just dropping them on Sign In with no context.
  const [sessionExpiredMsg, setSessionExpiredMsg] = useState(null)
  useEffect(() => {
    const msg = consumeSessionExpiredFlash()
    if (msg) setSessionExpiredMsg(msg)
  }, [])

  // Derived
  const step = pendingTwoFactor ? 2 : 1

  useEffect(() => {
    if (isAuthenticated) navigate(redirectTo, { replace: true })
  }, [isAuthenticated, navigate, redirectTo])

  // Clear error when switching between steps or typing
  useEffect(() => { clearError() }, [step])

  // Seed the resend countdown when we enter step 2 in email mode
  useEffect(() => {
    if (pendingTwoFactor?.method === 'email' && pendingTwoFactor.resend_after) {
      setResendCountdown(pendingTwoFactor.resend_after)
    }
  }, [pendingTwoFactor?.method, pendingTwoFactor?.resend_after])

  // Tick the countdown
  useEffect(() => {
    if (resendCountdown <= 0) return
    const id = setInterval(() => setResendCountdown(c => Math.max(0, c - 1)), 1000)
    return () => clearInterval(id)
  }, [resendCountdown])

  // ── Step 1 submit ───────────────────────────────────────────────────────────
  const handleCredentials = async (e) => {
    e.preventDefault()
    setResendMsg(null); setOtpValue('')
    chosenMethodRef.current = authMethod
    const result = await login(email.trim(), password, authMethod, remember)
    if (result.success && !result.require_2fa) {
      navigate(redirectTo)
    }
    // If require_2fa=true, pendingTwoFactor is now set → renders Step 2
  }

  // ── Step 2 submit ───────────────────────────────────────────────────────────
  // codeOverride lets OtpInput.onComplete pass the FRESH value, bypassing
  // any React state-batching delay.
  const verifySubmittingRef = useRef(false)
  const handleVerify = async (e, codeOverride) => {
    e?.preventDefault?.()
    if (verifySubmittingRef.current) return
    let code
    if (useBackupCode) {
      code = backupCodeValue.trim().toUpperCase()
      if (code.length !== 8 || isLoading) return
    } else {
      code = (codeOverride ?? otpValue).replace(/\D/g, '').slice(0, 6)
      if (code.length < 6 || isLoading) return
    }
    verifySubmittingRef.current = true
    try {
      const result = await verifyTwoFactor(
        pendingTwoFactor.temp_token,
        code,
        chosenMethodRef.current,
      )
      if (result.success) navigate(redirectTo)
      else { setOtpValue(''); setBackupCodeValue('') }
    } finally {
      verifySubmittingRef.current = false
    }
  }

  const handleResend = async () => {
    if (resendBusy || resendCountdown > 0) return
    setResendBusy(true); setResendMsg(null)
    const r = await resendEmail2faCode(pendingTwoFactor.temp_token)
    setResendBusy(false)
    if (r.success) {
      setResendMsg({ type: 'success', text: r.message || 'A new code has been sent.' })
      setResendCountdown(r.resend_after || 60)
    } else {
      setResendMsg({ type: 'error', text: r.error })
      if (r.retry_after) setResendCountdown(r.retry_after)
    }
  }

  // ── Shared card wrapper ─────────────────────────────────────────────────────
  const bgStyle = { backgroundImage: 'linear-gradient(to bottom, #e8f1f8, #d0e3f0)' }

  // ════════════════════════════════════════════════════════════════════════════
  // STEP 2 — 2FA verification (Authenticator app OR email)
  // ════════════════════════════════════════════════════════════════════════════
  if (step === 2) {
    const isEmailMethod = pendingTwoFactor.method === 'email'
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={bgStyle}>
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-sm">
          <div className="flex flex-col items-center mb-6">
            <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mb-3">
              {isEmailMethod
                ? <ShieldCheck className="w-7 h-7 text-[#358ECD]" />
                : <GoogleAuthIcon />}
            </div>
            <h2 className="text-2xl font-bold text-gray-800">2-Factor Authentication</h2>
            <p className="text-sm text-gray-500 mt-1 text-center">
              {isEmailMethod ? (
                <>We sent a 6-digit code to <strong>{pendingTwoFactor.sent_to}</strong>.<br />Enter it below to continue.</>
              ) : (
                <>Open <strong>Google Authenticator</strong> and enter the 6-digit code</>
              )}
            </p>
          </div>

          {error && (
            <div className="mb-4 px-4 py-2 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm text-center">
              {error}
            </div>
          )}

          {resendMsg && (
            <div className={`mb-4 px-4 py-2 rounded-lg text-sm text-center border ${
              resendMsg.type === 'success'
                ? 'bg-green-50 border-green-200 text-green-700'
                : 'bg-red-50 border-red-200 text-red-700'
            }`}>
              {resendMsg.text}
            </div>
          )}

          <form onSubmit={handleVerify} className="space-y-6">
            {useBackupCode ? (
              <div>
                <input
                  type="text"
                  value={backupCodeValue}
                  onChange={e => setBackupCodeValue(e.target.value.toUpperCase())}
                  placeholder="Enter 8-character backup code"
                  maxLength={8}
                  disabled={isLoading}
                  autoFocus
                  className="w-full text-center text-base font-mono tracking-widest border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:border-[#358ECD] disabled:bg-gray-100 uppercase"
                />
                <p className="text-xs text-gray-400 text-center mt-1.5">
                  Backup codes are 8 characters, e.g. <span className="font-mono">A3F2B9E1</span>
                </p>
              </div>
            ) : (
              <OtpInput value={otpValue} onChange={setOtpValue}
                onComplete={(fresh) => handleVerify(null, fresh)}
                disabled={isLoading} />
            )}

            <button
              type="submit"
              disabled={isLoading || (useBackupCode ? backupCodeValue.trim().length !== 8 : otpValue.length < 6)}
              className="w-full py-3 rounded-lg text-white font-bold text-sm tracking-wide
                         transition disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#358ECD' }}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader className="w-4 h-4 animate-spin" /> Verifying...
                </span>
              ) : 'CONFIRM'}
            </button>
          </form>

          {!isEmailMethod && (
            <div className="text-center">
              <button type="button"
                onClick={() => { setUseBackupCode(v => !v); setOtpValue(''); setBackupCodeValue('') }}
                className="text-xs text-gray-400 hover:text-[#358ECD] transition underline">
                {useBackupCode ? 'Use Google Authenticator instead' : "Can't access your app? Use a backup code"}
              </button>
            </div>
          )}

          {isEmailMethod && (
            <div className="mt-4 text-center text-sm">
              {resendCountdown > 0 ? (
                <span className="text-gray-500">Resend available in {resendCountdown}s</span>
              ) : (
                <button onClick={handleResend} disabled={resendBusy}
                  className="text-[#358ECD] hover:underline disabled:opacity-50">
                  {resendBusy ? 'Sending…' : 'Resend code'}
                </button>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={() => {
              useAuthStore.setState({ pendingTwoFactor: null, error: null })
              setOtpValue(''); setBackupCodeValue(''); setUseBackupCode(false)
              setResendMsg(null); setResendCountdown(0)
            }}
            className="mt-5 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mx-auto transition"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Sign In
          </button>
        </div>
      </div>
    )
  }

  // ════════════════════════════════════════════════════════════════════════════
  // STEP 1 — Email + Password
  // ════════════════════════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={bgStyle}>
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-sm">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Sign In</h2>

        {sessionExpiredMsg && (
          <div className="mb-4 px-4 py-3 bg-blue-50 border border-blue-200 text-blue-800 rounded-lg text-sm flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{sessionExpiredMsg}</span>
          </div>
        )}

        {error && (
          <div className="mb-4 px-4 py-2 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleCredentials} className="space-y-4">
          {/* Email */}
          <div>
            <label className="block text-sm text-gray-700 mb-1">Email</label>
            <input
              type="text"
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={isLoading}
              placeholder="Email"
              required
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm
                         focus:outline-none focus:ring-2 focus:ring-[#358ECD]/30 focus:border-[#358ECD]
                         disabled:bg-gray-100 transition"
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm text-gray-700 mb-1">Password</label>
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                disabled={isLoading}
                placeholder="Password"
                required
                className="w-full px-4 py-2.5 pr-10 border border-gray-300 rounded-lg text-sm
                           focus:outline-none focus:ring-2 focus:ring-[#358ECD]/30 focus:border-[#358ECD]
                           disabled:bg-gray-100 transition"
              />
              <button
                type="button"
                onClick={() => setShowPwd(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                tabIndex={-1}
              >
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Remember + Forgot */}
          <div className="flex items-center justify-between text-sm">
            <label className="flex items-center gap-2 text-gray-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={remember}
                onChange={e => setRemember(e.target.checked)}
                className="w-4 h-4 accent-[#358ECD]"
              />
              Remember Me
            </label>
            <button
              type="button"
              onClick={() => setShowForgot(true)}
              className="text-[#358ECD] hover:underline"
            >
              Forgot Password?
            </button>
          </div>

          {/* 2FA method picker */}
          <div className="pt-1">
            <p className="text-xs font-semibold text-gray-500 mb-2">2-Factor Authentication Method</p>
            <div className="flex gap-2">
              <button type="button" onClick={() => setAuthMethod('email')}
                className={`flex-1 py-2 px-3 rounded-lg font-semibold text-sm transition flex items-center justify-center gap-2 border-2 ${
                  authMethod === 'email'
                    ? 'border-[#358ECD] bg-[#358ECD] text-white'
                    : 'border-[#358ECD] text-[#358ECD] bg-white'
                }`}>
                Email
              </button>
              <button type="button" onClick={() => setAuthMethod('totp')}
                className={`flex-1 py-2 px-3 rounded-lg font-semibold text-sm transition flex items-center justify-center gap-2 border-2 ${
                  authMethod === 'totp'
                    ? 'border-[#358ECD] bg-[#358ECD] text-white'
                    : 'border-[#358ECD] text-[#358ECD] bg-white'
                }`}>
                Authenticator App
              </button>
            </div>
            <p className="text-[11px] text-gray-500 mt-1.5">
              Only used if you have 2FA enabled.
            </p>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 rounded-lg text-white font-bold text-sm tracking-wide
                       transition disabled:opacity-50 disabled:cursor-not-allowed mt-2"
            style={{ backgroundColor: '#358ECD' }}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader className="w-4 h-4 animate-spin" /> Signing In...
              </span>
            ) : 'CONTINUE'}
          </button>
        </form>

        <p className="mt-5 text-center text-sm text-gray-600">
          New to WQData LIVE?{' '}
          <button
            type="button"
            onClick={() => setShowRegister(true)}
            className="text-[#358ECD] font-semibold hover:underline"
          >
            Create an Account
          </button>
        </p>
      </div>

      <ForgotPasswordModal open={showForgot} onClose={() => setShowForgot(false)} />
      <CreateAccountModal
        isOpen={showRegister}
        onClose={() => setShowRegister(false)}
        onSwitchToSignIn={() => setShowRegister(false)}
      />
    </div>
  )
}
